/**
 * MedicationReferralDialog — Extracted from App.js, Feb 2026 (V2
 * Meds & Vitals migration Phase 2).
 *
 * This is the ~1,250-line "Medication Management Referral" dialog
 * that legacy App.js used to render inline.  Lifted out so:
 *   • Legacy App.js stops carrying 1,250 lines of unrelated UI
 *     in its monolith.
 *   • The V2 chart can mount its own instance natively (next
 *     iteration) and we can finally retire the
 *     `v2:open-medication-referral` window-event bridge.
 *
 * Extraction principle: PURE MECHANICAL LIFT.  Inside the dialog
 * body the variable names are unchanged — every external symbol
 * App.js used to provide via lexical scope is now received as a
 * prop with the SAME NAME.  This minimizes diff risk; semantic
 * refactors (e.g. routing the cross-tab "Add Med" / "Add Dx" /
 * "Append Med" actions through `useChartActions()`) are deferred
 * to follow-up PRs so any regression that surfaces here is
 * unambiguously caused by the extraction itself.
 *
 * Required props
 * ──────────────
 *   open                                    ← showMedicationReferralDialog
 *   setShowMedicationReferralDialog
 *   medicationReferralClient                ← the client being referred
 *   medicationReferralFormData / setMedicationReferralFormData
 *   selectedMedicationManagers / setSelectedMedicationManagers
 *   medicationReferralTemplate              ← form template (lazy)
 *   fetchMedicationReferralTemplate         ← fetches template on first open
 *   generatingAISummary / setGeneratingAISummary
 *   setClientMedications                    ← optimistic meds list update
 *   setSelectedClient                       ← used by Add-Med shortcut
 *   setShowMedicationForm                   ← used by Add-Med shortcut
 *   setShowDiagnosisForm                    ← used by Add-Dx shortcut
 *   selectedNote (nullable)
 *   setSelectedNote
 *   setActiveFormFields
 *   isRefreshingDiagnoses
 *   setIsRefreshingDiagnoses
 *   users                                   ← for med-manager picker
 *   user                                    ← current user (auth)
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, X, Plus, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { ReferralFormField } from './referral/ReferralFormField';
import { formatProviderName } from '../../utils/userHelpers';
import { fmtLocalDate } from '../../utils/insuranceDates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MedicationReferralDialog = (props) => {
  // Destructure props into the SAME names the lifted JSX expects.
  // This is intentionally verbose — it makes the cut-and-paste
  // boundary explicit and lets a reviewer verify each external
  // dependency is plumbed in.
  const {
    open: showMedicationReferralDialog,
    setShowMedicationReferralDialog,
    medicationReferralClient,
    medicationReferralFormData,
    setMedicationReferralFormData,
    selectedMedicationManagers,
    setSelectedMedicationManagers,
    medicationReferralTemplate,
    fetchMedicationReferralTemplate,
    generatingAISummary,
    setGeneratingAISummary,
    setClientMedications,
    setSelectedClient,
    setShowMedicationForm,
    setShowDiagnosisForm,
    selectedNote,
    setSelectedNote,
    setActiveFormFields,
    isRefreshingDiagnoses,
    setIsRefreshingDiagnoses,
    users,
    user,
    // Additional props pulled from App.js scope.  See the lifted
    // JSX below — each is referenced verbatim by the old code.
    medicationManagerUsers,
    clientMedications,
    clientDiagnoses,
    clientEligibility,
    allClientsForLookup,
    selectedTemplate,
  } = props;

  // Suppress no-unused-vars for any prop a particular code path
  // doesn't reach (e.g. selectedNote-related branches are only
  // executed inside Compulsory Assessment notes).
  void selectedNote; void setSelectedNote; void setActiveFormFields;
  void isRefreshingDiagnoses; void setIsRefreshingDiagnoses;
  void user; void users;

  // Track whether we've attempted (and finished) a template fetch
  // for the current dialog session.  Lets us distinguish:
  //   • Dialog just opened → still loading template (spinner)
  //   • Fetch resolved but no active medication_referral template
  //     exists in DB → render an actionable empty state instead of
  //     an infinite spinner.
  // Reset whenever the dialog closes so a subsequent open reruns
  // the fetch (admin may have just created the template).
  //
  // NOTE: `fetchMedicationReferralTemplate` is intentionally accessed
  // via a ref instead of useEffect deps because callers commonly
  // pass it as an inline async arrow (recreated every parent render).
  // If we listed it in deps, the effect would re-fire on every parent
  // re-render and the previous render's cleanup would flip an
  // in-flight `cancelled` flag — making .finally() short-circuit and
  // never setting templateFetchAttempted, leaving the dialog stuck
  // on the spinner forever.  See iteration_267 RCA.
  const [templateFetchAttempted, setTemplateFetchAttempted] = useState(false);
  const [templateFetching, setTemplateFetching] = useState(false);
  const fetchTemplateRef = useRef(fetchMedicationReferralTemplate);
  useEffect(() => {
    fetchTemplateRef.current = fetchMedicationReferralTemplate;
  }, [fetchMedicationReferralTemplate]);

  useEffect(() => {
    if (!showMedicationReferralDialog) {
      // Reset on close so reopening triggers a fresh fetch.
      setTemplateFetchAttempted(false);
      setTemplateFetching(false);
      return undefined;
    }
    if (medicationReferralTemplate) {
      setTemplateFetchAttempted(true);
      return undefined;
    }
    const fn = fetchTemplateRef.current;
    if (typeof fn !== 'function') {
      setTemplateFetchAttempted(true);
      return undefined;
    }
    let cancelled = false;
    setTemplateFetching(true);
    Promise.resolve(fn())
      .catch((err) => {
        console.error('[MedicationReferralDialog] template fetch failed', err);
      })
      .finally(() => {
        if (cancelled) return;
        setTemplateFetching(false);
        setTemplateFetchAttempted(true);
      });
    return () => { cancelled = true; };
  }, [showMedicationReferralDialog, medicationReferralTemplate]);

  return (
    <>
      {/* Medication Referral Dialog */}
      <Dialog open={showMedicationReferralDialog} onOpenChange={(open) => {
        setShowMedicationReferralDialog(open);
      }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="med-referral-dialog">
          <DialogHeader>
            <DialogTitle>Medication Management Referral</DialogTitle>
            <DialogDescription>
              Submit a referral request for medication management consultation and prescriptions.
            </DialogDescription>
          </DialogHeader>
          
          {!medicationReferralTemplate && !templateFetchAttempted ? (
            <div className="text-center py-8" data-testid="med-referral-loading">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Loading medication referral template...</p>
            </div>
          ) : !medicationReferralTemplate && templateFetchAttempted ? (
            <div
              className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 space-y-3"
              data-testid="med-referral-empty-state"
            >
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold mb-1">No active Medication Referral form template found.</p>
                  <p className="text-amber-800">
                    The Medication Referral dialog uses a Form Template (form type{' '}
                    <code className="bg-amber-100 px-1 rounded">medication_referral</code>) for its
                    dynamic question fields, combined with the medication-manager picker shown here.
                  </p>
                  <p className="text-amber-800 mt-2">
                    Ask an administrator to create an active template in{' '}
                    <strong>Admin → Form Templates</strong> with form type{' '}
                    <code className="bg-amber-100 px-1 rounded">medication_referral</code>, then
                    reopen this dialog.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="med-referral-empty-close"
                  onClick={() => setShowMedicationReferralDialog(false)}
                >
                  Close
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={async (e) => {
              e.preventDefault();
              
              if (selectedMedicationManagers.length === 0) {
                toast.error("Please select a medication manager");
                return;
              }
              
              // Validate required form fields
              const missingFields = medicationReferralTemplate.form_fields.filter(field => 
                field.required && !medicationReferralFormData[field.id]
              );
              
              if (missingFields.length > 0) {
                toast.error(`Please fill in all required fields: ${missingFields.map(f => f.label).join(', ')}`);
                return;
              }
              
              try {
                const response = await axios.post(`${API}/medication-referral-requests`, {
                  client_id: medicationReferralClient.id,
                  template_id: medicationReferralTemplate.id,
                  form_data: medicationReferralFormData,
                  requested_for: selectedMedicationManagers
                });
                
                // Check if there's a warning about previously denied referral
                if (response.data?.warning) {
                  toast.warning(response.data.warning, { duration: 8000 });
                }
                toast.success("Medication referral request submitted successfully!");
                setShowMedicationReferralDialog(false);
                setMedicationReferralFormData({});
                setSelectedMedicationManagers([]);
              } catch (error) {
                console.error("Failed to submit medication referral:", error);
                toast.error(`Failed to submit: ${error.response?.data?.detail || error.message}`);
              }
            }}>
              <div className="bg-purple-50 border border-purple-200 rounded-md p-3 text-sm">
                <p className="text-purple-800">
                  <strong>Client:</strong> {medicationReferralClient?.full_name} {medicationReferralClient?.mrn && `(MRN: ${medicationReferralClient.mrn})`}
                </p>
                <p className="text-purple-700 mt-1">
                  <strong>Form:</strong> {medicationReferralTemplate.name}
                </p>
                <p className="text-purple-600 mt-1 text-xs">
                  ℹ️ Relevant information has been auto-filled from the client&apos;s chart. Please review and update as needed.
                </p>
              </div>

              {/* Medication Manager Selection */}
              <div>
                <Label htmlFor="medication-managers">Assign to Medication Manager *</Label>
                <Select
                  value={selectedMedicationManagers.length > 0 ? selectedMedicationManagers[0] : ''}
                  onValueChange={(value) => {
                    setSelectedMedicationManagers([value]);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a medication manager..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Medication Managers</SelectItem>
                    {medicationManagerUsers.map(manager => (
                      <SelectItem key={manager.id} value={manager.id}>
                        {formatProviderName(manager)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {medicationManagerUsers.length === 0 && (
                  <p className="text-sm text-gray-500 mt-1">No medication managers available.</p>
                )}
              </div>

              {/* Dynamic Form Fields from template - Grid Layout */}
              <div className="space-y-4">
                {(() => {
                  let skipNext = false;
                  return medicationReferralTemplate.form_fields.map((field, index) => {
                    // Skip if this field was already rendered as part of a pair
                    if (skipNext) {
                      skipNext = false;
                      return null;
                    }
                    
                    // Check conditional logic - should this field be displayed?
                  if (field.conditionalField) {
                    const conditionalFieldValue = medicationReferralFormData[field.conditionalField];
                    const conditionMet = (() => {
                      if (!conditionalFieldValue) return false;
                      
                      switch (field.conditionalOperator || 'equals') {
                        case 'equals':
                          return conditionalFieldValue === field.conditionalValue;
                        case 'contains':
                          // For multiselect, check if array contains value
                          if (Array.isArray(conditionalFieldValue)) {
                            return conditionalFieldValue.includes(field.conditionalValue);
                          }
                          return String(conditionalFieldValue).includes(field.conditionalValue);
                        case 'not_equals':
                          return conditionalFieldValue !== field.conditionalValue;
                        default:
                          return false;
                      }
                    })();
                    
                    // Don't render if condition not met
                    if (!conditionMet) {
                      return null;
                    }
                  }
                  
                  // Determine if field should be full-width or in grid
                  const fullWidthFields = [
                    'Insurance Information',
                    'Current Medications',
                    'Additional Diagnoses (Include Substance Abuse)',
                    'Known Allergies/Adverse Reactions to Medications',
                    'Mental Health Service Details',
                    'Comments on effectiveness of mental health services',
                    'Other Medical Considerations'
                  ];
                  
                  // Section headers and dividers should always be full width
                  // Otherwise, respect the width setting from form builder
                  const fieldWidth = parseInt(field.width || '100');
                  const isFullWidth = field.type === 'section_header' || field.type === 'divider' || field.type === 'client_visibility_divider' || fieldWidth === 100;
                  
                  // Check if next field should also be in grid
                  const nextField = medicationReferralTemplate.form_fields[index + 1];
                  const nextFieldWidth = nextField ? parseInt(nextField.width || '100') : 100;
                  const nextIsFullWidth = nextField ? (nextField.type === 'section_header' || nextField.type === 'divider' || nextField.type === 'client_visibility_divider' || nextFieldWidth === 100) : true;
                  
                  // Create grid pairs for compact fields
                  if (!isFullWidth && !nextIsFullWidth && nextField) {
                    skipNext = true; // Mark next field to be skipped since we're rendering it here
                    return (
                      <div key={field.id} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* First field */}
                        <div>
                          {field.type !== 'checkbox' && (
                            <Label htmlFor={`field-${field.id}`}>
                              {field.label} {field.required && <span className="text-red-500">*</span>}
                            </Label>
                          )}
                          <ReferralFormField
                            field={field}
                            formData={medicationReferralFormData}
                            setFormData={setMedicationReferralFormData}
                          />
                        </div>

                        {/* Second field */}
                        <div>
                          {nextField.type !== 'checkbox' && (
                            <Label htmlFor={`field-${nextField.id}`}>
                              {nextField.label} {nextField.required && <span className="text-red-500">*</span>}
                            </Label>
                          )}
                          <ReferralFormField
                            field={nextField}
                            formData={medicationReferralFormData}
                            setFormData={setMedicationReferralFormData}
                          />
                        </div>
                      </div>
                    );
                  }
                  
                  // Skip if this was already rendered as part of a pair
                  if (!isFullWidth && index > 0) {
                    const prevField = medicationReferralTemplate.form_fields[index - 1];
                    const prevIsFullWidth = prevField.type === 'textarea' || fullWidthFields.includes(prevField.label);
                    if (!prevIsFullWidth && index % 2 === 1) {
                      return null;
                    }
                  }
                  
                  // Render full-width or single fields
                  return (
                    <div key={field.id}>
                      {/* Section headers and layout elements don't need labels */}
                      {field.type === 'section_header' && (
                        <div 
                          className="border-b-2 pb-1 mb-3 mt-5 min-h-[28px]"
                          style={{ borderColor: field.backgroundColor || '#3b82f6' }}
                        >
                          {field.label && (
                            <h3 
                              className="text-base font-semibold uppercase tracking-wide"
                              style={{ color: field.backgroundColor || '#3b82f6' }}
                            >
                              {field.label}
                            </h3>
                          )}
                        </div>
                      )}
                      
                      {field.type === 'divider' && (
                        <hr className="my-6 border-gray-300" />
                      )}
                      
                      {field.type === 'client_visibility_divider' && (
                        <div className="my-6 flex items-center justify-center">
                          <div className="flex-1 border-t-2 border-dashed border-teal-400"></div>
                          <span className="px-4 text-sm text-teal-600 font-medium whitespace-nowrap">--- CLIENT PORTAL / SUMMARY START ---</span>
                          <div className="flex-1 border-t-2 border-dashed border-teal-400"></div>
                        </div>
                      )}
                      
                      {/* Regular fields with labels - exclude section_header, divider, and checkbox (checkbox has inline label) */}
                      {field.type !== 'section_header' && field.type !== 'divider' && field.type !== 'client_visibility_divider' && field.type !== 'checkbox' && (
                        <Label htmlFor={`field-${field.id}`}>
                          {field.label} {field.required && <span className="text-red-500">*</span>}
                        </Label>
                      )}
                      
                      {field.type === 'text' && (
                        <ReferralFormField
                          field={field}
                          formData={medicationReferralFormData}
                          setFormData={setMedicationReferralFormData}
                        />
                      )}
                      
                      {field.type === 'date' && (
                        <ReferralFormField
                          field={field}
                          formData={medicationReferralFormData}
                          setFormData={setMedicationReferralFormData}
                        />
                      )}
                      
                      {field.type === 'time' && (
                        <ReferralFormField
                          field={field}
                          formData={medicationReferralFormData}
                          setFormData={setMedicationReferralFormData}
                        />
                      )}
                      
                      {field.type === 'textarea' && (
                        <div>
                          <ReferralFormField
                            field={field}
                            formData={medicationReferralFormData}
                            setFormData={setMedicationReferralFormData}
                          />
                          {/* AI Summarize button for service history fields */}
                          {field.label === 'Mental Health Service Details' && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="mt-2 border-purple-300 text-purple-700 hover:bg-purple-50"
                              disabled={generatingAISummary}
                              onClick={async () => {
                                setGeneratingAISummary(true);
                                try {
                                  const response = await axios.post(`${API}/clients/${medicationReferralClient.id}/ai-summary`);
                                  setMedicationReferralFormData({
                                    ...medicationReferralFormData,
                                    [field.id]: response.data.summary
                                  });
                                  toast.success("AI summary generated!");
                                } catch (error) {
                                  console.error("Failed to generate AI summary:", error);
                                  toast.error("Failed to generate AI summary");
                                } finally {
                                  setGeneratingAISummary(false);
                                }
                              }}
                            >
                              {generatingAISummary ? (
                                <>⏳ Generating...</>
                              ) : (
                                <>✨ AI Summarize History</>
                              )}
                            </Button>
                          )}
                        </div>
                      )}
                      
                      {/* Handle special client data types as read-only display sections */}
                      {field.type === 'client_medications' && (
                        <div className="border rounded-md p-3 bg-blue-50 min-h-[100px]">
                          {(() => {
                            const activeMedications = clientMedications?.filter(m => m.is_active === true) || [];
                            
                            // Handler to discontinue a medication
                            const handleDiscontinueMedRef = async (med) => {
                              if (!window.confirm(`Discontinue ${med.name || med.medication_name}?`)) return;
                              try {
                                await axios.patch(`${API}/medications/${med.id}/discontinue`);
                                toast.success(`${med.name || med.medication_name} discontinued`);
                                // Refresh medications list
                                const clientId = medicationReferralClient?.id || selectedNote?.client_id;
                                if (clientId) {
                                  const medsResponse = await axios.get(`${API}/clients/${clientId}/medications`);
                                  setClientMedications(prev => {
                                    const otherMeds = prev.filter(m => m.client_id !== clientId);
                                    return [...otherMeds, ...(medsResponse.data || [])];
                                  });
                                }
                              } catch (error) {
                                console.error('Failed to discontinue medication:', error);
                                toast.error('Failed to discontinue medication');
                              }
                            };
                            
                            return (
                              <div>
                                <Label className="text-sm font-semibold text-gray-700">{field.label}</Label>
                                {activeMedications.length > 0 ? (
                                  <table className="w-full text-sm mt-2 border bg-white">
                                    <thead className="bg-gray-100">
                                      <tr>
                                        <th className="text-left p-2 border">Medication</th>
                                        <th className="text-left p-2 border">Dosage</th>
                                        <th className="text-left p-2 border">Frequency</th>
                                        <th className="text-left p-2 border">Prescriber</th>
                                        <th className="text-left p-2 border">Purpose</th>
                                        <th className="text-center p-2 border w-24">Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {activeMedications.map((med, idx) => (
                                        <tr key={idx} className="bg-white">
                                          <td className="p-2 border">{med.name || med.medication_name}</td>
                                          <td className="p-2 border">{med.dosage || '-'}</td>
                                          <td className="p-2 border">{med.frequency || '-'}</td>
                                          <td className="p-2 border">{med.prescriber || '-'}</td>
                                          <td className="p-2 border">{med.purpose || '-'}</td>
                                          <td className="p-2 border text-center">
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="sm"
                                              className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                                              onClick={() => handleDiscontinueMedRef(med)}
                                              title="Discontinue medication"
                                            >
                                              <X className="h-3.5 w-3.5 mr-1" />
                                              <span className="text-xs">D/C</span>
                                            </Button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                ) : (
                                  <p className="text-sm text-gray-500 mt-2 italic">No medications on file</p>
                                )}
                                
                                {/* Add Medication Button */}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="mt-3 border-teal-300 text-teal-700 hover:bg-teal-50"
                                  disabled={!medicationReferralClient && !allClientsForLookup.find(c => c.id === selectedNote?.client_id)}
                                  onClick={() => {
                                    // Set the client for the medication dialog
                                    const client = medicationReferralClient || allClientsForLookup.find(c => c.id === selectedNote?.client_id);
                                    if (client) {
                                      setSelectedClient(client);
                                      setShowMedicationForm(true);
                                    } else {
                                      toast.error('Unable to add medication - client data not loaded. Please refresh the page.');
                                    }
                                  }}
                                >
                                  <Plus className="h-4 w-4 mr-1" />
                                  Add Medication
                                </Button>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      
                      {field.type === 'client_diagnoses' && (
                        <div className="border rounded-md p-3 bg-blue-50 min-h-[100px]">
                          {(() => {
                            const noteClientId = selectedNote?.client_id || medicationReferralClient?.id;
                            
                            // Use saved diagnoses from field.value if available, otherwise use live data
                            let diagnosesToShow = [];
                            try {
                              if (field.value && typeof field.value === 'string' && field.value.startsWith('[')) {
                                diagnosesToShow = JSON.parse(field.value);
                              } else if (Array.isArray(field.value)) {
                                diagnosesToShow = field.value;
                              } else {
                                // Fall back to live data (filtered for active only)
                                diagnosesToShow = clientDiagnoses?.filter(d => d.is_active !== false) || [];
                              }
                            } catch (e) {
                              diagnosesToShow = clientDiagnoses?.filter(d => d.is_active !== false) || [];
                            }
                            
                            // Helper to get user name
                            const getUserName = (userId, cachedName) => {
                              if (cachedName) return cachedName;
                              const foundUser = users.find(u => u.id === userId);
                              return foundUser ? (foundUser.full_name || `${foundUser.first_name || ''} ${foundUser.last_name || ''}`.trim()) : 'Unknown';
                            };
                            
                            return (
                              <div>
                                <Label className="text-sm font-semibold text-gray-700">{field.label}</Label>
                                {diagnosesToShow && diagnosesToShow.length > 0 ? (
                                  <table className="w-full text-sm mt-2 border bg-white">
                                    <thead className="bg-gray-100">
                                      <tr>
                                        <th className="text-left p-2 border">Code</th>
                                        <th className="text-left p-2 border">Description</th>
                                        <th className="text-left p-2 border">Tag</th>
                                        <th className="text-left p-2 border">Provider(s)</th>
                                        <th className="text-left p-2 border">Date</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {diagnosesToShow.map((diag, idx) => (
                                        <tr key={idx} className="bg-white">
                                          <td className="p-2 border">{diag.code || '-'}</td>
                                          <td className="p-2 border">{diag.description || '-'}</td>
                                          <td className="p-2 border">
                                            {diag.diagnosis_tag ? (
                                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                                diag.diagnosis_tag === 'differential' ? 'bg-purple-100 text-purple-700' :
                                                diag.diagnosis_tag === 'working' ? 'bg-amber-100 text-amber-700' :
                                                diag.diagnosis_tag === 'rule_out' ? 'bg-red-100 text-red-700' :
                                                diag.diagnosis_tag === 'provisional' ? 'bg-sky-100 text-sky-700' :
                                                'bg-gray-100 text-gray-600'
                                              }`}>
                                                {diag.diagnosis_tag === 'differential' ? 'Diff' : diag.diagnosis_tag === 'working' ? 'Wrk' : diag.diagnosis_tag === 'rule_out' ? 'R/O' : diag.diagnosis_tag === 'provisional' ? 'Prov' : diag.diagnosis_tag}
                                              </span>
                                            ) : '-'}
                                          </td>
                                          <td className="p-2 border">{diag.provider_names?.length > 0 ? diag.provider_names.join(', ') : getUserName(diag.assigned_by, diag.assigned_by_name)}</td>
                                          <td className="p-2 border">{diag.start_date ? fmtLocalDate(diag.start_date) : '-'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                ) : (
                                  <p className="text-sm text-gray-500 mt-2 italic">No diagnoses on file</p>
                                )}
                                
                                {/* Add Diagnosis Button */}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="mt-3 border-teal-300 text-teal-700 hover:bg-teal-50"
                                  disabled={!allClientsForLookup.find(c => c.id === noteClientId)}
                                  onClick={() => {
                                    // Set the client for the diagnosis dialog
                                    const client = allClientsForLookup.find(c => c.id === noteClientId);
                                    if (client) {
                                      setSelectedClient(client);
                                      // Use setTimeout to ensure state updates before opening dialog
                                      setTimeout(() => {
                                        setShowDiagnosisForm(true);
                                      }, 50);
                                    } else {
                                      toast.error('Unable to add diagnosis - client data not loaded. Please refresh the page.');
                                    }
                                  }}
                                >
                                  <Plus className="h-4 w-4 mr-1" />
                                  Add Diagnosis
                                </Button>

                                {/* Refresh Diagnoses Button — Comp Assessments only.
                                    Re-pulls the client's current active diagnoses
                                    into this note's prefill field + refreshes
                                    note.diagnosis_snapshot for billing. Use case:
                                    provider opens the assessment before finalizing
                                    the diagnoses, then adds them on the chart later. */}
                                {(selectedTemplate?.form_type === 'Comprehensive Assessments') &&
                                 selectedNote?.id &&
                                 !['signed','co_signed','billed','ready_for_billing','locked'].includes(selectedNote.status) && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="mt-3 ml-2 text-teal-700 hover:bg-teal-50"
                                    disabled={isRefreshingDiagnoses === selectedNote.id}
                                    onClick={async () => {
                                      try {
                                        setIsRefreshingDiagnoses(selectedNote.id);
                                        const resp = await axios.post(
                                          `${API}/notes/${selectedNote.id}/refresh-diagnoses`,
                                          {},
                                          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                                        );
                                        // Re-fetch the note so activeFormFields picks up the new field values
                                        const updated = await axios.get(
                                          `${API}/notes/${selectedNote.id}`,
                                          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                                        );
                                        const updatedNote = updated.data;
                                        const parsed = typeof updatedNote.content === 'string'
                                          ? JSON.parse(updatedNote.content)
                                          : updatedNote.content;
                                        if (parsed?.fields) {
                                          setActiveFormFields(parsed.fields);
                                        }
                                        setSelectedNote(updatedNote);
                                        toast.success(
                                          `Diagnoses refreshed (${resp.data.diagnosis_count || 0} on file)`
                                        );
                                      } catch (err) {
                                        const detail = err?.response?.data?.detail || err?.message || 'Failed to refresh diagnoses';
                                        toast.error(detail);
                                      } finally {
                                        setIsRefreshingDiagnoses(null);
                                      }
                                    }}
                                    data-testid={`refresh-diagnoses-btn-${field.id || 'comp'}`}
                                    title="Re-pull diagnoses from the client chart. Use this if you added diagnoses after starting the assessment."
                                  >
                                    <RefreshCw
                                      className={`h-4 w-4 mr-1 ${isRefreshingDiagnoses === selectedNote.id ? 'animate-spin' : ''}`}
                                    />
                                    {isRefreshingDiagnoses === selectedNote.id ? 'Refreshing…' : 'Refresh diagnoses'}
                                  </Button>
                                )}

                                {/* "Last refreshed" timestamp — Comp Assessments only.
                                    Renders below the action buttons so multi-provider
                                    teams can see when the snapshot last picked up new
                                    chart-side diagnoses. */}
                                {(selectedTemplate?.form_type === 'Comprehensive Assessments') &&
                                 selectedNote?.diagnoses_refreshed_at && (
                                  <p
                                    className="mt-2 text-[11px] text-gray-500 italic"
                                    data-testid={`diagnoses-last-refreshed-${field.id || 'comp'}`}
                                  >
                                    Diagnoses last refreshed{' '}
                                    {(() => {
                                      try {
                                        const when = new Date(selectedNote.diagnoses_refreshed_at);
                                        const date = when.toLocaleDateString();
                                        const time = when.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                        return `${date} at ${time}`;
                                      } catch (_e) {
                                        return selectedNote.diagnoses_refreshed_at;
                                      }
                                    })()}
                                    {selectedNote.diagnoses_refreshed_by && (() => {
                                      const refresher = users.find(u => u.id === selectedNote.diagnoses_refreshed_by);
                                      const name = refresher
                                        ? (refresher.full_name || `${refresher.first_name || ''} ${refresher.last_name || ''}`.trim())
                                        : null;
                                      return name ? ` by ${name}` : '';
                                    })()}
                                  </p>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      
                      {field.type === 'client_diagnoses_selector' && (
                        <div className="border rounded-md p-3 bg-gray-50 min-h-[100px]">
                          {(() => {
                            const noteClientId = selectedNote?.client_id || medicationReferralClient?.id;
                            
                            // Get the saved value (may be JSON string of selected diagnoses)
                            let savedDiagnoses = [];
                            try {
                              const savedValue = field.value;
                              if (savedValue && typeof savedValue === 'string' && savedValue.startsWith('[')) {
                                savedDiagnoses = JSON.parse(savedValue);
                              } else if (Array.isArray(savedValue)) {
                                // If it's an array of IDs, filter from clientDiagnoses
                                const selectedIds = savedValue;
                                savedDiagnoses = clientDiagnoses.filter(d => selectedIds.includes(d.id));
                              }
                            } catch (e) {
                              void 0 && console.log('Could not parse diagnoses value');
                            }
                            
                            // If editing, show selector; if viewing saved note, show saved diagnoses
                            const displayDiagnoses = savedDiagnoses.length > 0 ? savedDiagnoses : [];
                            
                            const getUserName = (userId, cachedName) => {
                              if (cachedName) return cachedName;
                              const foundUser = users.find(u => u.id === userId);
                              return foundUser ? (foundUser.full_name || `${foundUser.first_name || ''} ${foundUser.last_name || ''}`.trim()) : 'Unknown';
                            };
                            
                            return (
                              <div className="space-y-3">
                                {displayDiagnoses.length > 0 ? (
                                  <div className="space-y-2">
                                    {displayDiagnoses.map((diag, idx) => (
                                      <div key={idx} className="flex items-center justify-between text-sm border-b pb-2 last:border-0">
                                        <div className="flex-1">
                                          <div className="font-medium text-gray-900">
                                            {diag.is_primary && <span className="text-xs bg-teal-100 text-teal-800 px-1.5 py-0.5 rounded mr-2">Primary</span>}
                                            {diag.code && <span className="text-teal-700">{diag.code}</span>}
                                            {diag.code && diag.description && <span> - </span>}
                                            <span>{diag.description}</span>
                                          </div>
                                          <div className="flex items-center gap-4 text-xs text-gray-600 mt-1">
                                            {diag.start_date && (
                                              <span>Since: {fmtLocalDate(diag.start_date)}</span>
                                            )}
                                            {(diag.provider_names?.length > 0) && (
                                              <span>Provider(s): {diag.provider_names.join(', ')}</span>
                                            )}
                                            {(!diag.provider_names || diag.provider_names.length === 0) && (diag.assigned_by || diag.assigned_by_name) && (
                                              <span>Added by: {getUserName(diag.assigned_by, diag.assigned_by_name)}</span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500 italic">No diagnoses selected</p>
                                )}
                                
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="mt-2 border-teal-300 text-teal-700 hover:bg-teal-50"
                                  disabled={!allClientsForLookup.find(c => c.id === noteClientId)}
                                  onClick={() => {
                                    const client = allClientsForLookup.find(c => c.id === noteClientId);
                                    if (client) {
                                      setSelectedClient(client);
                                      // Use setTimeout to ensure state updates before opening dialog
                                      setTimeout(() => {
                                        setShowDiagnosisForm(true);
                                      }, 50);
                                    } else {
                                      toast.error('Unable to add diagnosis - client data not loaded. Please refresh the page.');
                                    }
                                  }}
                                >
                                  <Plus className="h-4 w-4 mr-1" />
                                  Add New Diagnosis
                                </Button>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      
                      {field.type === 'client_insurance' && (
                        <div className="border rounded-md p-3 bg-gray-50 min-h-[100px]">
                          {(() => {
                            void 0 && console.log('client_insurance field - medicationReferralClient:', medicationReferralClient);
                            void 0 && console.log('client_insurance field - clientEligibility:', clientEligibility);
                            
                            // Use medicationReferralClient instead of selectedClient
                            const clientData = medicationReferralClient;
                            
                            // Check if client has the new insurances array structure
                            const hasInsurancesArray = clientData?.insurances && Array.isArray(clientData.insurances) && clientData.insurances.length > 0;
                            
                            if (hasInsurancesArray) {
                              // Use the new insurances array structure
                              return (
                                <div className="space-y-3">
                                  {clientData.insurances.filter(ins => ins.company).map((ins, idx) => {
                                    // Map priority to readable labels
                                    let priorityLabel = ins.priority;
                                    if (ins.priority === '1' || ins.priority === 1) priorityLabel = 'Primary';
                                    else if (ins.priority === '2' || ins.priority === 2) priorityLabel = 'Secondary';
                                    else if (ins.priority === '3' || ins.priority === 3) priorityLabel = 'Tertiary';
                                    else if (!priorityLabel) priorityLabel = 'Primary';
                                    
                                    return (
                                      <div key={idx} className="text-sm border-b pb-2 last:border-0">
                                        <div className="font-medium text-gray-900">
                                          <span className="text-blue-700">{priorityLabel}:</span> {ins.company}
                                        </div>
                                        {(ins.member_id || ins.group_number) && (
                                          <div className="text-xs text-gray-600 mt-1">
                                            {ins.member_id && <span>Member ID: {ins.member_id}</span>}
                                            {ins.group_number && ins.member_id && <span className="ml-3">•</span>}
                                            {ins.group_number && <span className={ins.member_id ? "ml-1" : ""}>Group: {ins.group_number}</span>}
                                          </div>
                                        )}
                                        {ins.status && (
                                          <div className="text-xs mt-1">
                                            <span className="font-medium text-gray-700">Eligibility:</span>{' '}
                                            <span className={ins.status === 'active' ? 'text-green-600' : 'text-orange-600'}>
                                              {ins.status.charAt(0).toUpperCase() + ins.status.slice(1)}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            } else {
                              // Fall back to old flat fields structure
                              const insuranceData = [];
                              
                              if (clientData?.primary_insurance) {
                                const ins = {
                                  priority: 'Primary',
                                  insurance_company: clientData.primary_insurance
                                };
                                
                                if (clientData.primary_member_id && clientData.primary_member_id.trim()) {
                                  ins.member_id = clientData.primary_member_id;
                                }
                                if (clientData.primary_group_number && clientData.primary_group_number.trim()) {
                                  ins.group_number = clientData.primary_group_number;
                                }
                                
                                insuranceData.push(ins);
                              }
                              
                              if (clientData?.secondary_insurance) {
                                const ins = {
                                  priority: 'Secondary',
                                  insurance_company: clientData.secondary_insurance
                                };
                                
                                if (clientData.secondary_member_id && clientData.secondary_member_id.trim()) {
                                  ins.member_id = clientData.secondary_member_id;
                                }
                                if (clientData.secondary_group_number && clientData.secondary_group_number.trim()) {
                                  ins.group_number = clientData.secondary_group_number;
                                }
                                
                                insuranceData.push(ins);
                              }
                              
                              if (clientData?.tertiary_insurance) {
                                const ins = {
                                  priority: 'Tertiary',
                                  insurance_company: clientData.tertiary_insurance
                                };
                                
                                if (clientData.tertiary_member_id && clientData.tertiary_member_id.trim()) {
                                  ins.member_id = clientData.tertiary_member_id;
                                }
                                if (clientData.tertiary_group_number && clientData.tertiary_group_number.trim()) {
                                  ins.group_number = clientData.tertiary_group_number;
                                }
                                
                                insuranceData.push(ins);
                              }
                              
                              if (insuranceData.length > 0) {
                                return (
                                  <div className="space-y-3">
                                    {insuranceData.map((ins, idx) => (
                                      <div key={idx} className="text-sm border-b pb-2 last:border-0">
                                        <div className="font-medium text-gray-900">
                                          <span className="text-blue-700">{ins.priority}:</span> {ins.insurance_company}
                                        </div>
                                        {(ins.member_id || ins.group_number) && (
                                          <div className="text-xs text-gray-600 mt-1">
                                            {ins.member_id && <span>Member ID: {ins.member_id}</span>}
                                            {ins.group_number && ins.member_id && <span className="ml-3">•</span>}
                                            {ins.group_number && <span className={ins.member_id ? "ml-1" : ""}>Group: {ins.group_number}</span>}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                    {/* Show eligibility status - either from formal check or client status */}
                                    <div className="text-xs mt-2 pt-2 border-t">
                                      <span className="font-medium text-gray-700">Eligibility Status:</span>{' '}
                                      {clientEligibility ? (
                                        <>
                                          <span className={clientEligibility.is_eligible ? 'text-green-600 font-medium' : 'text-orange-600 font-medium'}>
                                            {clientEligibility.is_eligible ? 'Active' : 'Pending'}
                                          </span>
                                          {clientEligibility.check_date && (
                                            <span className="text-gray-600 ml-2">
                                              (verified {new Date(clientEligibility.check_date).toLocaleDateString()})
                                            </span>
                                          )}
                                        </>
                                      ) : (
                                        <span className="text-orange-600 font-medium">
                                          {clientData?.eligibility_status === 'active' ? 'Active' : 'Pending'}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              } else {
                                return <p className="text-sm text-gray-500 italic">No insurance on file</p>;
                              }
                            }
                          })()}
                        </div>
                      )}
                      
                      {(field.type === 'select'
                        || field.type === 'multiselect'
                        || field.type === 'radio'
                        || field.type === 'checkbox') && (
                        <ReferralFormField
                          field={field}
                          formData={medicationReferralFormData}
                          setFormData={setMedicationReferralFormData}
                        />
                      )}
                    </div>
                  );
                });
              })()}
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowMedicationReferralDialog(false);
                    setMedicationReferralFormData({});
                    setSelectedMedicationManagers([]);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-purple-600 hover:bg-purple-700"
                  disabled={selectedMedicationManagers.length === 0}
                >
                  Submit Referral
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MedicationReferralDialog;
